import {
    Box,
    Typography,
    Grid,
    Button,
    Divider,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    InputBase,
    Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckIcon from "@mui/icons-material/Check";
import React, { useState } from "react";
import UploadedThreeFilesDisplay from "./UploadedThreeFilesDisplay";
import metadata_demo from "../../assets/examples/metadata_demo.csv";
import { validateMetadataFileStrictDetailed as validateMetadata } from "../General/validateMetadataFileFormat";
import { uploadFileTus, uploadMultipleFilesTus } from "../hooks/UseTusUpload";
import { apiRequest } from "../api/apiClient";
import ProcessingModal from "./Modales/ProcessingModal";
import UploadCompleteModal from "./Modales/UploadCompleteModal";
import ErrorModal from "./Modales/ErrorModal";
import { convertToCSVFile } from "../General/GeneralFuntions";

export default function SequencingReadsCard({
    onUploadMetadata,
    onUploadR1,
    onUploadR2,
    onAnalyze,
    genomes = [
        "Acinetobacter baumannii",
        "Klebsiella pneumoniae",
        "Staphylococcus aureus",
    ],
}) {
    const [metadataFile, setMetadataFile] = useState(null);
    const [metadataError, setMetadataError] = useState(null);
    const [r1File, setR1File] = useState(null);
    const [r1Error, setR1Error] = useState(null);
    const [r2File, setR2File] = useState(null);
    const [r2Error, setR2Error] = useState(null);
    const [selectedGenome, setSelectedGenome] = useState("");
    const [acceptedTos, setAcceptedTos] = useState(false);
    const [email, setEmail] = useState("");
    const [formErrors, setFormErrors] = useState([]);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([]);
    const [openLoadModal, setOpenLoadModal] = useState(false);
    const [openUploadCompleteModal, setOpenUploadCompleteModal] = useState(false);
    const [openErrorModal, setOpenErrorModal] = useState(false);
    const [errorTitle, setErrorTitle] = useState("Connection issues");
    const [errorMessage, setErrorMessage] = useState(
        "Please check your internet connection and try downloading your files again."
    );

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const genomeHasError = submitAttempted && !selectedGenome;
    const metadataHasError = submitAttempted && (!metadataFile || Boolean(metadataError));
    const r1HasError = submitAttempted && (!r1File || Boolean(r1Error));
    const r2HasError = submitAttempted && Boolean(r2Error);
    const emailHasError = submitAttempted && !isValidEmail(email);
    const tosHasError = submitAttempted && !acceptedTos;

    const showGreenCheck = (condition) =>
        condition ? (
            <Box
                sx={{
                    width: "55px",
                    height: "55px",
                    borderRadius: "50%",
                    border: "1px solid #20BC71",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ml: 2,
                }}
            >
                <CheckIcon sx={{ color: "#20BC71", fontSize: "30px" }} />
            </Box>
        ) : null;

    const removeError = (text) => {
        setFormErrors((prev) => prev.filter((err) => err !== text));
    };

    const handleUploadMetadata = async (event) => {
        const input = event.target;

        if (!input.files || input.files.length === 0) {
            return;
        }

        const file = input.files[0];
        const validation = await validateMetadata(file);

        if (validation.ok) {
            const csvData = await convertToCSVFile(file);

            setMetadataError(null);
            setMetadataFile(csvData);
            removeError("Upload metadata file");
            removeError("Metadata file is incomplete or incorrect");
            onUploadMetadata?.(file);
        } else {
            setMetadataFile(null);
            setMetadataError(validation.errors);
        }

        input.value = "";
    };

    const handleUploadR1 = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fileName = file.name;
        const pattern = /^.+_R1(_.+)?\.fastq\.gz$/i;
        const isValid = pattern.test(fileName);

        if (!isValid) {
            setR1Error(
                "Invalid filename. Accepted formats:\n" +
                "sampleID_R1.fastq.gz\n" +
                "sampleID_*_R1.fastq.gz\n" +
                "sampleID*_R1_*.fastq.gz"
            );
            setR1File(null);
        } else {
            setR1Error(null);
            setR1File(file);
            removeError("Upload FASTQ R1 file");
            removeError("FASTQ R1 filename is invalid");
            onUploadR1?.(file);
        }

        e.target.value = "";
    };

    const handleUploadR2 = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fileName = file.name;
        const pattern = /^.+_R2(_.+)?\.fastq\.gz$/i;
        const isValid = pattern.test(fileName);

        if (!isValid) {
            setR2Error(
                "Invalid filename. Accepted formats:\n" +
                "sampleID_R2.fastq.gz\n" +
                "sampleID_*_R2.fastq.gz\n" +
                "sampleID*_R2_*.fastq.gz"
            );
            setR2File(null);
        } else {
            setR2Error(null);
            setR2File(file);
            removeError("FASTQ R2 filename is invalid");
            onUploadR2?.(file);
        }

        e.target.value = "";
    };

    const validateForm = async () => {
        const errors = [];

        if (!selectedGenome) errors.push("Select a reference genome");

        if (!metadataFile) errors.push("Upload metadata file");
        if (metadataError) errors.push("Metadata file is incomplete or incorrect");

        if (!r1File) errors.push("Upload FASTQ R1 file");
        if (r1Error) errors.push("FASTQ R1 filename is invalid");

        if (r2Error) errors.push("FASTQ R2 filename is invalid");

        if (!acceptedTos) errors.push("Accept Terms of Service");
        if (!isValidEmail(email)) errors.push("Enter a valid email");

        return { ok: errors.length === 0, errors };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitAttempted(true);

        const { ok, errors } = await validateForm();
        setFormErrors(errors);

        if (!ok) return;

        initUploadProgress();
        setOpenUploadCompleteModal(false);
        setOpenErrorModal(false);
        setOpenLoadModal(true);

        try {
            const fileMetadataID = await uploadFileTus({
                file: metadataFile,
                token: "CAMBIA_ESTE_TOKEN",
                email: email,
                bacterium: selectedGenome,
                analyze: "Reads",
                onProgress: updateUploadProgress,
            });

            const fileNames = await uploadAllFiles();
            const typeLayout = fileNames.length === 2 ? "paired" : "single";

            const payload = {
                metadataFile: fileMetadataID.tus_id,
                email: email,
                reference: selectedGenome,
                analyze: "reads",
                layout: typeLayout,
                filename_equiv: fileNames,
            };

            const metadataPost = await apiRequest("submit_run.php", {
                method: "POST",
                body: payload,
            });

            setOpenLoadModal(false);

            resetForm(); // 👈 limpia todo antes de abrir modal de éxito
            setOpenUploadCompleteModal(true);

            onAnalyze?.(metadataPost);
            return metadataPost;
        } catch (err) {
            console.error(err);

            setOpenLoadModal(false);
            setOpenUploadCompleteModal(false);

            const isConnectionError =
                err?.message === "connection_error" ||
                err?.code === "connection_error";

            if (isConnectionError) {
                setErrorTitle("Connection issues");
                setErrorMessage(
                    "Please check your internet connection and try uploading your files again."
                );
            } else {
                setErrorTitle("Unexpected error");
                setErrorMessage("Something went wrong. Please try again.");
            }

            resetForm(); // 👈 limpia todo antes de abrir modal de error
            setOpenErrorModal(true);
        }
    };

    const resetForm = () => {
        setMetadataFile(null);
        setMetadataError(null);

        setR1File(null);
        setR1Error(null);

        setR2File(null);
        setR2Error(null);

        setSelectedGenome("");
        setAcceptedTos(false);
        setEmail("");
        setFormErrors([]);
        setSubmitAttempted(false);
        setUploadProgress([]);
    };

    async function uploadAllFiles() {
        try {
            const filesToUpload = [r1File, r2File].filter(Boolean);

            const fileNames = await uploadMultipleFilesTus({
                files: filesToUpload,
                token: "CAMBIA_ESTE_TOKEN",
                email: email,
                bacterium: selectedGenome,
                analyze: "Reads",
                onProgress: updateUploadProgress,
            });

            return fileNames;
        } catch (error) {
            console.error("Upload error:", error);
            throw new Error("connection_error");
        }
    }

    const initUploadProgress = () => {
        const files = [r1File, r2File].filter(Boolean);

        const initial = files.map((file) => ({
            fileName: file.name,
            percentage: 0,
            bytesUploaded: 0,
            bytesTotal: file.size || 0,
        }));

        setUploadProgress(initial);
    };

    const updateUploadProgress = ({ fileName, percentage, bytesUploaded, bytesTotal }) => {
        setUploadProgress((prev) => {
            const exists = prev.some((item) => item.fileName === fileName);

            if (!exists) {
                return [
                    ...prev,
                    { fileName, percentage, bytesUploaded, bytesTotal },
                ];
            }

            return prev.map((item) =>
                item.fileName === fileName
                    ? { ...item, percentage, bytesUploaded, bytesTotal }
                    : item
            );
        });
    };

    return (
        <Box
            sx={{
                bgcolor: "#000",
                color: "#fff",
                borderRadius: "36px",
                border: "1px solid rgba(255,255,255,0.18)",
                p: { xs: 3, md: 5 },
                width: "100%",
                maxWidth: "1920px",
                mx: "auto",
                minWidth: "400px",
            }}
        >
            <Typography sx={{ mb: 3 }} variant="h4">
                Hosts and isolation source
            </Typography>

            <Grid container sx={{ width: "100%" }} spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box sx={{ width: "100%", minHeight: { xs: "auto", md: "200px" } }}>
                        <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                            Each row should contain the following fields:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 3, opacity: 0.9 }}>
                            <Typography variant="body1">
                                <li>The sampleID (the identifier for each sample)</li>
                                <li>The country of origin</li>
                                <li>The host</li>
                                <li>The isolation source</li>
                                <li>The collection date (format: dd/mm/yyyy)</li>
                                <li>Authors</li>
                                <li style={{ textDecoration: "none", color: "#20BC71" }}>
                                    <a
                                        href={metadata_demo}
                                        download="Metadata_Template.csv"
                                        style={{ textDecoration: "none", color: "#20BC71" }}
                                    >
                                        Download metadata template
                                    </a>
                                </li>
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box
                        sx={{
                            width: "100%",
                            minHeight: { xs: "auto", md: "200px" },
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                        }}
                    >
                        <Grid>
                            <Typography sx={{ opacity: 0.9, mb: 2 }} variant="body1">
                                Each field must not contain spaces.
                            </Typography>
                            <Typography sx={{ opacity: 0.9, mb: 2 }} variant="body1">
                                The accepted file formats are: txt, tsv, csv, xls, xlsx
                            </Typography>
                        </Grid>

                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                        border: metadataHasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid transparent",
                                        color: "#000",
                                        borderRadius: 9999,
                                        px: 3,
                                        py: 1.25,
                                        width: "100%",
                                        maxWidth: "415px",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        "&:hover": {
                                            bgcolor: "#111",
                                            boxShadow: "none",
                                            color: "#fff",
                                            border: "1px solid #fff",
                                        },
                                    }}
                                    color="iconoBlanco"
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Upload your metadata file
                                    </Typography>

                                    <FileUploadIcon sx={{ width: "30px", height: "30px" }} />

                                    <input
                                        type="file"
                                        hidden
                                        accept=".txt,.tsv,.csv,.xls,.xlsx"
                                        onChange={handleUploadMetadata}
                                    />
                                </Button>

                                {metadataFile && !metadataError && showGreenCheck(true)}
                            </Box>

                            <Collapse in={Boolean(metadataError)}>
                                {metadataError && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            p: 2,
                                            border: "1px solid #ff4d4f",
                                            borderRadius: 2,
                                            bgcolor: "rgba(255, 77, 79, 0.1)",
                                        }}
                                    >
                                        <Typography
                                            sx={{ color: "#ff4d4f", fontWeight: "bold", mb: 1 }}
                                            variant="body2"
                                        >
                                            The metadata file is incomplete or incorrect:
                                        </Typography>
                                        <ul
                                            style={{
                                                margin: 0,
                                                paddingLeft: "20px",
                                                color: "#ff4d4f",
                                            }}
                                        >
                                            {Array.isArray(metadataError) ? (
                                                metadataError.map((err, index) => (
                                                    <li key={index}>
                                                        <Typography variant="body1">{err}</Typography>
                                                    </li>
                                                ))
                                            ) : (
                                                <li>
                                                    <Typography variant="body1">
                                                        {metadataError}
                                                    </Typography>
                                                </li>
                                            )}
                                        </ul>
                                    </Box>
                                )}
                            </Collapse>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.25)" }} />

            <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
                Sequences
            </Typography>

            <Grid container sx={{ width: "100%" }} spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box sx={{ width: "100%" }}>
                        <Typography sx={{ opacity: 0.9, mb: 1 }} variant="body1">
                            The same number of R1 and R2 files must be provided.
                        </Typography>
                        <Typography sx={{ opacity: 0.9, mb: 1 }} variant="body1">
                            The name should follow the nomenclature:
                        </Typography>

                        <Typography sx={{ opacity: 0.9, mb: 1 }} variant="body1">
                            Option A:
                        </Typography>
                        <Box
                            component="ul"
                            sx={{ m: 0, pl: 3, opacity: 0.9, fontSize: "18px", mb: 2 }}
                        >
                            <Typography variant="body2">
                                <li>sampleID_R1.fastq.gz</li>
                                <li>sampleID_R2.fastq.gz</li>
                            </Typography>
                        </Box>

                        <Typography sx={{ opacity: 0.9, mb: 1 }} variant="body1">
                            Option B:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 3, opacity: 0.9, mb: 2 }}>
                            <Typography variant="body2">
                                <li>sampleID_*_R1.fastq.gz</li>
                                <li>sampleID_*_R2.fastq.gz</li>
                            </Typography>
                        </Box>

                        <Typography sx={{ opacity: 0.9, mb: 1 }} variant="body1">
                            Option C:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 3, opacity: 0.9 }}>
                            <Typography variant="body2">
                                <li>sampleID*_R1_*.fastq.gz</li>
                                <li>sampleID*_R2_*.fastq.gz</li>
                            </Typography>
                        </Box>

                        <Typography variant="body2">
                            <p>(*) can be any identifier</p>
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            mt: 3,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Select
                            fullWidth
                            displayEmpty
                            IconComponent={ExpandMoreIcon}
                            value={selectedGenome}
                            onChange={(e) => {
                                setSelectedGenome(e.target.value);
                                removeError("Select a reference genome");
                            }}
                            sx={{
                                borderRadius: 0,
                                color: "#fff",
                                ".MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                    borderTop: genomeHasError
                                        ? "1px solid #ff4d4f"
                                        : "1px solid #fff",
                                    borderBottom: genomeHasError
                                        ? "1px solid #ff4d4f"
                                        : "1px solid #fff",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderTop: genomeHasError
                                        ? "1px solid #ff4d4f"
                                        : "1px solid #fff",
                                    borderBottom: genomeHasError
                                        ? "1px solid #ff4d4f"
                                        : "1px solid #fff",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderTop: genomeHasError
                                        ? "1px solid #ff4d4f"
                                        : "1px solid #fff",
                                    borderBottom: genomeHasError
                                        ? "1px solid #ff4d4f"
                                        : "1px solid #fff",
                                },
                                ".MuiSelect-icon": {
                                    color: genomeHasError ? "#ff4d4f" : "#fff",
                                    fontSize: 28,
                                    right: 8,
                                },
                                width: "100%",
                                maxWidth: "520px",
                                height: "59px",
                            }}
                            renderValue={(v) => (v ? v : "Select a reference genome")}
                        >
                            {genomes.map((g) => (
                                <MenuItem key={g} value={g}>
                                    {g}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Collapse in={genomeHasError}>
                        {genomeHasError && (
                            <Typography
                                sx={{ color: "#ff4d4f", fontSize: "14px", mt: 1, ml: 1 }}
                            >
                                Please select a reference genome.
                            </Typography>
                        )}
                    </Collapse>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%" }}>
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                        border: r1HasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid transparent",
                                        color: "#000",
                                        borderRadius: 9999,
                                        px: 3,
                                        py: 1.25,
                                        width: "100%",
                                        maxWidth: "415px",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        "&:hover": {
                                            bgcolor: "#111",
                                            boxShadow: "none",
                                            color: "#fff",
                                            border: "1px solid #fff",
                                        },
                                    }}
                                    color="iconoBlanco"
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Upload your FASTQ files (R1)
                                    </Typography>

                                    <FileUploadIcon sx={{ width: "30px", height: "30px" }} />

                                    <input
                                        type="file"
                                        hidden
                                        onChange={handleUploadR1}
                                    />
                                </Button>

                                {r1File && !r1Error && showGreenCheck(true)}
                            </Box>

                            <Collapse in={Boolean(r1Error) || (submitAttempted && !r1File)}>
                                {(Boolean(r1Error) || (submitAttempted && !r1File)) && (
                                    <Typography
                                        sx={{ color: "#ff4d4f", fontSize: "14px", mt: 1, ml: 1 }}
                                    >
                                        {r1Error || "Please upload a FASTQ R1 file."}
                                    </Typography>
                                )}
                            </Collapse>
                        </Box>

                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                        border: r2HasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid transparent",
                                        color: "#000",
                                        borderRadius: 9999,
                                        px: 3,
                                        py: 1.25,
                                        width: "100%",
                                        maxWidth: "415px",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 1,
                                        "&:hover": {
                                            bgcolor: "#111",
                                            boxShadow: "none",
                                            color: "#fff",
                                            border: "1px solid #fff",
                                        },
                                    }}
                                    color="iconoBlanco"
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Upload your FASTQ files (R2)*
                                    </Typography>

                                    <FileUploadIcon
                                        sx={{ width: "30px", height: "30px", flexShrink: 0 }}
                                    />

                                    <input
                                        type="file"
                                        hidden
                                        onChange={handleUploadR2}
                                    />
                                </Button>

                                {r2File && !r2Error && showGreenCheck(true)}
                            </Box>

                            <Collapse in={Boolean(r2Error)}>
                                {r2Error && (
                                    <Typography
                                        sx={{ color: "#ff4d4f", fontSize: "14px", mt: 1, ml: 1 }}
                                    >
                                        {r2Error}
                                    </Typography>
                                )}
                            </Collapse>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.25)" }} />

            <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
                Contact
            </Typography>

            <Grid container sx={{ width: "100%" }} spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box sx={{ width: "100%" }}>
                        <Typography variant="body1" sx={{ width: "80%", mt: 3 }}>
                            Personal data collected through this medium will not be stored in any
                            database nor will it be subject to further processing. Your
                            information will be used exclusively to send a notification when the
                            processing is over to the email address provided.
                        </Typography>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                        Provide the email where the results will be sent.
                    </Typography>
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            mt: 3,
                        }}
                    >
                        <InputBase
                            placeholder="Email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                removeError("Enter a valid email");
                            }}
                            sx={{
                                border: emailHasError
                                    ? "1px solid #ff4d4f"
                                    : "1px solid #fff",
                                bgcolor: "#000",
                                color: "#fff",
                                borderRadius: 9999,
                                px: 3,
                                py: 1.25,
                                width: "100%",
                                maxWidth: "415px",
                            }}
                        />
                        {isValidEmail(email) && showGreenCheck(true)}
                    </Box>

                    <Collapse in={emailHasError}>
                        {emailHasError && (
                            <Typography
                                sx={{ color: "#ff4d4f", fontSize: "14px", mt: 1, ml: 1 }}
                            >
                                Please enter a valid email.
                            </Typography>
                        )}
                    </Collapse>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.25)" }} />

            <Grid container sx={{ width: "100%" }} spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={acceptedTos}
                                onChange={(e) => {
                                    setAcceptedTos(e.target.checked);
                                    removeError("Accept Terms of Service");
                                }}
                                sx={{
                                    border: tosHasError
                                        ? "2px solid #ff4d4f"
                                        : "2px solid #fff",
                                    borderRadius: "4px",
                                    width: "15px",
                                    height: "15px",
                                    mt: 3,
                                }}
                                icon={
                                    <Box
                                        sx={{
                                            width: "15px",
                                            height: "15px",
                                            border: "2px solid transparent",
                                            borderRadius: "4px",
                                        }}
                                    />
                                }
                                checkedIcon={
                                    <Box
                                        sx={{
                                            width: "15px",
                                            height: "15px",
                                            backgroundColor: "#000",
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                background: "#20BC71",
                                                width: "12px",
                                                height: "12px",
                                                borderRadius: "2px",
                                            }}
                                        />
                                    </Box>
                                }
                            />
                        }
                        label={
                            <Typography variant="body1" sx={{ width: "80%", mt: 3 }}>
                                I accept that the Latin America Resistance Atlas collects
                                pathogen identification, antimicrobial resistance gene list, date,
                                geographical region and the genomic sequence. The summary of
                                the data collected will be added to the statistics shown in the
                                Browse section.
                            </Typography>
                        }
                        sx={{ alignItems: "flex-start", gap: 2, mb: 2 }}
                    />

                    <Collapse in={tosHasError}>
                        {tosHasError && (
                            <Typography
                                sx={{ color: "#ff4d4f", fontSize: "14px", mt: 1, ml: 1 }}
                            >
                                Please accept the Terms of Service.
                            </Typography>
                        )}
                    </Collapse>
                </Grid>

                <Grid
                    size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    <UploadedThreeFilesDisplay
                        metaData={metadataFile}
                        r1File={r1File}
                        r2File={r2File}
                        labels={["Metadata", "FASTQ R1", "FASTQ R2"]}
                        onRemoveVariable1={() => {
                            setMetadataFile(null);
                            setMetadataError(null);
                        }}
                        onRemoveR1={() => {
                            setR1File(null);
                            setR1Error(null);
                        }}
                        onRemoveR2={() => {
                            setR2File(null);
                            setR2Error(null);
                        }}
                    />

                    <Button
                        variant="contained"
                        sx={{
                            width: "100%",
                            maxWidth: "270px",
                            borderRadius: "9999px",
                            color: "black",
                            height: "56px",
                            mt: 2,
                            "&:hover": { bgcolor: "#fff", boxShadow: "none", color: "#000" },
                        }}
                        color="green"
                        onClick={(e) => {
                            handleSubmit(e);
                        }}
                        disabled={openLoadModal}
                    >
                        <Typography variant="body1">ANALYZE</Typography>
                    </Button>

                    <Collapse in={submitAttempted && formErrors.length > 0}>
                        {submitAttempted && formErrors.length > 0 && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#ff4d4f",
                                    mt: 1.5,
                                    maxWidth: "415px",
                                }}
                            >
                                Please review the form. Some required fields are missing or invalid.
                            </Typography>
                        )}
                    </Collapse>
                </Grid>
            </Grid>

            <ProcessingModal
                open={openLoadModal}
                onClose={() => {
                    setOpenLoadModal(false);
                }}
                filesProgress={uploadProgress}
            />

            <UploadCompleteModal
                open={openUploadCompleteModal}
                onClose={() => {
                    setOpenUploadCompleteModal(false);
                }}
            />

            <ErrorModal
                open={openErrorModal}
                onClose={() => {
                    setOpenErrorModal(false);
                }}
                title={errorTitle}
                message={errorMessage}
            />
        </Box>
    );
}
